import { type ListData } from 'core/domain/Request'
import { type HttpClientConfig, type HttpClientInterface } from 'core/infra/http/HttpClient'

import { type Employee, type NewEmployee } from '../domain/Employee'
import { type PreferredTrackingMethod } from '../domain/PreferredTrackingMethod'
import { type Tractor } from '../domain/Tractor'
import {
  mapApiToEmployee,
  mapApiToEmployeeList,
  mapApiToPreferredTrackingMethod,
  mapEmployeeToApi,
  mapPreferredTrackingMethodToApi,
  type RawEmployee,
  type RawPreferredTrackingMethod,
  type RawTrackingHistory,
} from './CarrierMapper'
import {
  mapApiToTractor,
  mapApiToTractors,
  mapTractorNumberToApi,
  type RawTractor,
} from './CarrierTractorMapper'

type CarrierRepositoryDependencies = {
  httpClient: HttpClientInterface
}

export type AddEmployeeParams = {
  carrierUUID: string
  employee: NewEmployee
  config?: Partial<HttpClientConfig>
}

export type RawAddEmployeeResponseError = {
  phone_number: string[]
}

export type AddEmployeeResponseError = {
  phoneNumber?: string
}

export type getCarrierTractorsParams = {
  carrierUUID: string
  query?: string
  config?: Partial<HttpClientConfig>
}

export type CreateCarrierTractorNumberParams = {
  carrierUUID: string
  tractorNumber: string
  config?: Partial<HttpClientConfig>
}

export class CarrierRepository {
  private readonly httpClient: HttpClientInterface

  constructor({ httpClient }: CarrierRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getCarrierEmployees(
    carrierUUID: string,
    config?: Partial<HttpClientConfig>
  ): Promise<Employee[]> {
    const endpoint = `/carriers/${carrierUUID}/employees`
    const { data } = await this.httpClient.get<RawEmployee[]>(endpoint, config)

    return mapApiToEmployeeList(data)
  }

  async addEmployee({ carrierUUID, employee, config }: AddEmployeeParams): Promise<Employee> {
    const endpoint = `/carriers/${carrierUUID}/employees`
    const payload = mapEmployeeToApi(employee)
    const { data } = await this.httpClient.post<RawEmployee>(endpoint, payload, config)

    return mapApiToEmployee(data)
  }

  async getCarrierTractorsNumbers({ carrierUUID, query, config }: getCarrierTractorsParams) {
    const endpoint = `/carriers/${carrierUUID}/tractor-numbers?search=${query || ''}`
    const { data } = await this.httpClient.get<ListData<RawTractor>>(endpoint, config)
    return mapApiToTractors(data.data)
  }

  async createCarrierTractorNumber({
    carrierUUID,
    tractorNumber,
    config,
  }: CreateCarrierTractorNumberParams): Promise<Tractor> {
    const endpoint = `/carriers/${carrierUUID}/tractor-numbers`
    const payload = mapTractorNumberToApi(tractorNumber)
    const { data } = await this.httpClient.post<RawTractor>(endpoint, payload, config)

    return mapApiToTractor(data)
  }

  async hasPreviousTracking(config?: Partial<HttpClientConfig>): Promise<boolean> {
    const { data } = await this.httpClient.get<RawTrackingHistory>(
      'api/v2/shipperguide/carriers/tracking-method-history',
      config
    )
    return data.has_previous_tracking
  }

  async getPreferredTrackingMethod(
    config?: Partial<HttpClientConfig>
  ): Promise<PreferredTrackingMethod | undefined> {
    const { data } = await this.httpClient.get<RawPreferredTrackingMethod>(
      'api/v2/shipperguide/carriers/carrier-managed-profile',
      config
    )
    return mapApiToPreferredTrackingMethod(data)
  }

  async updatePreferredTrackingMethod(
    preferredTrackingMethod: PreferredTrackingMethod,
    config?: Partial<HttpClientConfig>
  ): Promise<PreferredTrackingMethod | undefined> {
    const payload = mapPreferredTrackingMethodToApi(preferredTrackingMethod)

    const { data } = await this.httpClient.patch<RawPreferredTrackingMethod | null>(
      'api/v2/shipperguide/carriers/carrier-managed-profile',
      payload,
      config
    )
    return mapApiToPreferredTrackingMethod(data)
  }
}
